/**
 * Procedural film grain, generated in the browser at startup.
 *
 * The site previously used the stock `feTurbulence` data-URI that every CSS
 * "add noise" article reaches for. It has two tells. It is one fixed tile, so
 * the texture slides rather than resamples — real grain is a fresh dice roll
 * of silver crystals every frame. And fractal noise is smooth and uniform,
 * where film grain is sparse and clumpy: discrete crystals that clot together,
 * with clear emulsion between them.
 *
 * So the tiles are built here instead. Several frames are generated up front
 * and cycled, which resamples the texture the way an emulsion does, and each
 * theme gets its own crystal size, sparsity and colour cast — 35mm horror
 * stock is not the same material as a phosphor CRT.
 */

export type GrainSpec = {
  /** Tile edge in px. Smaller reads as finer stock. */
  size: number;
  /** Fraction of pixels carrying a crystal. Low values leave clear emulsion. */
  density: number;
  /** Amplitude of the grain against neutral. */
  contrast: number;
  /** How much crystals clot together, 0 = pure white noise, 1 = fully clumped. */
  clump: number;
  /** RGB bias, so grain can carry a colour cast rather than being pure grey. */
  tint: [number, number, number];
  /** Distinct tiles to cycle through. */
  frames: number;
};

/** Neutral for `overlay` blending — this value leaves the backdrop untouched. */
const NEUTRAL = 128;

/**
 * Box-blur a scalar field with wraparound, so the blurred result still tiles.
 * Blurring is what turns independent pixels into clumps.
 */
const blurWrapped = (src: Float32Array, size: number): Float32Array => {
  const out = new Float32Array(src.length);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let sum = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          // `+ size` before the modulo keeps negative offsets in range.
          const sy = (y + dy + size) % size;
          const sx = (x + dx + size) % size;
          sum += src[sy * size + sx];
        }
      }
      out[y * size + x] = sum / 9;
    }
  }
  return out;
};

/** One grain tile as a PNG data URI, or null if canvas is unavailable. */
const renderTile = (spec: GrainSpec): string | null => {
  const { size, density, contrast, clump, tint } = spec;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const count = size * size;
  const white = new Float32Array(count);
  for (let i = 0; i < count; i++) white[i] = Math.random();

  // Two independent fields: one decides how bright each crystal is, the other
  // decides where there are crystals at all. Clumping both — rather than just
  // the brightness — is what stops the sparsity looking like TV static.
  const clumped = blurWrapped(white, size);
  const mask = blurWrapped(
    Float32Array.from({ length: count }, () => Math.random()),
    size
  );

  const image = ctx.createImageData(size, size);
  const data = image.data;

  // Blurring pulls values toward the mean, so the mask needs a threshold
  // relative to its own range rather than to `density` directly. 0.5 is the
  // mean of a uniform field; this offsets from there.
  const cut = 0.5 + (0.5 - density) * 0.5;

  for (let i = 0; i < count; i++) {
    const value = white[i] * (1 - clump) + clumped[i] * clump;
    const signed = (value - 0.5) * contrast * 255;
    const p = i * 4;
    data[p] = NEUTRAL + signed * tint[0];
    data[p + 1] = NEUTRAL + signed * tint[1];
    data[p + 2] = NEUTRAL + signed * tint[2];
    // Fully transparent where there is no crystal: with `overlay`, zero alpha
    // is a true no-op, so the emulsion between grains stays clean.
    data[p + 3] = mask[i] > cut ? 255 : 0;
  }

  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL("image/png");
};

const cache = new Map<string, string[]>();

/**
 * Grain tiles for a spec, generated once per spec and reused.
 *
 * Cached because this runs on every theme change, and regenerating on each
 * switch would put a visible hitch right where the cross-fade is meant to be
 * smooth.
 */
export const grainFrames = (key: string, spec: GrainSpec): string[] => {
  const hit = cache.get(key);
  if (hit) return hit;

  const frames: string[] = [];
  for (let i = 0; i < spec.frames; i++) {
    const tile = renderTile(spec);
    if (tile) frames.push(tile);
  }
  cache.set(key, frames);
  return frames;
};
